import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { can } from '../lib/perms';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Store,
  Users,
  Printer,
  Link2,
  ChevronRight,
  ArrowLeft,
  Percent,
  ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';

const settingsPages = [
  {
    path: '/settings/roles',
    label: 'Roles & Permissions',
    description: 'Choose what each role can see and do',
    icon: ShieldCheck,
    adminOnly: true
  },
  {
    path: '/settings/branches',
    label: 'Branches',
    description: 'Manage restaurant branches and sync from Loyverse',
    icon: Store,
    perm: 'settings.branches'
  },
  {
    path: '/settings/users',
    label: 'Users',
    description: 'Manage users, branch & supplier access',
    icon: Users,
    perm: 'settings.users'
  },
  {
    path: '/settings/printers',
    label: 'Printers',
    description: 'Configure receipt printers for order printing',
    icon: Printer,
    perm: 'settings.printers'
  },
  {
    path: '/settings/loyverse',
    label: 'Loyverse Integration',
    description: 'Manage API key and POS connection settings',
    icon: Link2,
    adminOnly: true
  },
  {
    path: '/settings/benchmark-margins',
    label: 'Benchmark Margins',
    description: 'Set target cost percentages for recipe categories',
    icon: Percent,
    adminOnly: true
  }
];

const SettingsHub = () => {
  const { user } = useAuth();

  const visiblePages = settingsPages.filter(
    p => (p.perm ? can(user, p.perm) : !p.adminOnly || user?.role === 'master_admin')
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage your account and system configuration</p>
      </div>

      {/* Profile Card */}
      <Card data-testid="settings-profile-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-semibold">{user?.name}</h3>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <Badge className="mt-1.5" variant="secondary">
                {user?.role?.replace('_', ' ')}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visiblePages.map((page) => {
          const Icon = page.icon;
          return (
            <Link key={page.path} to={page.path} data-testid={`settings-link-${page.path.split('/').pop()}`}>
              <Card className="hover:border-primary/40 hover:shadow-md transition-all cursor-pointer h-full">
                <CardContent className="pt-6 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground">{page.label}</p>
                    <p className="text-sm text-muted-foreground">{page.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* System Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Application</p>
              <p className="font-medium">TMC Stock App</p>
            </div>
            <div>
              <p className="text-muted-foreground">Version</p>
              <p className="font-medium">1.0.0</p>
            </div>
            <div>
              <p className="text-muted-foreground">VAT Rate</p>
              <p className="font-medium">15%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Currency</p>
              <p className="font-medium">ZAR (R)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const SettingsSubPageWrapper = ({ children, title }) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <Link to="/settings">
          <Button variant="ghost" size="icon" data-testid="settings-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <p className="text-sm text-muted-foreground">
            <Link to="/settings" className="hover:text-foreground transition-colors">Settings</Link>
            {' / '}
            <span className="text-foreground">{title}</span>
          </p>
        </div>
      </div>
      {children}
    </div>
  );
};

export { SettingsHub, SettingsSubPageWrapper };
export default SettingsHub;
